import React, { useState, useEffect } from 'react';
import { Phone, PhoneOff } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn } from '../../lib/utils';

interface CallStatusBarProps {
  callState?: 'idle' | 'ringing' | 'active' | 'incoming' | 'ended';
  duration?: number;
  remoteNumber?: string;
  candidateName?: string;
  isMuted?: boolean;
  onMuteToggle?: () => void;
  onHangup?: () => void;
  onEndCall?: () => void; // Alias for onHangup for backward compatibility
}

export const CallStatusBar: React.FC<CallStatusBarProps> = ({
  callState = 'idle',
  duration = 0,
  remoteNumber,
  candidateName,
  isMuted: _isMuted = false,
  onMuteToggle: _onMuteToggle,
  onHangup,
  onEndCall,
}) => {
  const handleHangup = onHangup || onEndCall;
  const [localDuration, setLocalDuration] = useState(duration);

  // Update duration every second when call is active
  useEffect(() => {
    if (callState === 'active') {
      const interval = setInterval(() => {
        setLocalDuration((prev) => prev + 1);
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setLocalDuration(duration);
    }
  }, [callState, duration]);

  // Don't show bar if no active call
  if (!callState || callState === 'idle' || callState === 'ended') {
    return null;
  }

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCallStateDisplay = () => {
    switch (callState) {
      case 'incoming':
        return '📞 Incoming Call';
      case 'ringing':
        return '📞 Calling...';
      case 'active':
        return '📞 Call Active';
      default:
        return '📞 Call';
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border z-50 shadow-lg animate-in slide-in-from-bottom-5 duration-300">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Call info */}
          <div className="flex items-center gap-3">
            {/* Status indicator */}
            <div
              className={cn(
                'w-3 h-3 rounded-full',
                callState === 'active' && 'bg-green-500 animate-pulse',
                callState === 'ringing' && 'bg-yellow-500 animate-pulse',
                callState === 'incoming' && 'bg-blue-500 animate-pulse'
              )}
            />

            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-muted-foreground" />
              
              <div>
                <div className="text-sm font-medium">
                  {getCallStateDisplay()}
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <span>
                    {candidateName || remoteNumber || 'Unknown'}
                  </span>
                  {callState === 'active' && (
                    <>
                      <span>•</span>
                      <span>{formatDuration(localDuration)}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {handleHangup && (
              <Button
                onClick={handleHangup}
                variant="destructive"
                size="sm"
                className="gap-2 hover:scale-105 transition-transform"
              >
                <PhoneOff className="w-4 h-4" />
                End Call
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

